/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { domUtils, classnames as cx, propsUtils, bidiUtils } from '@ba-ui-toolkit/utils';
import { Tooltip as CarbonTooltip } from 'carbon';
import { uniqueId } from 'lodash';
import styles from './TooltipPopup.scss';

const { sanitize } = propsUtils;
const tooltipPopupClassName = styles.tooltipPopupClassName.replace(/"/g, '');
const { resolveBaseTextDir } = bidiUtils;

export default class TooltipPopup extends Component {
	constructor(props) {
		super(props);
		this.tooltip = document.createElement('div');
		this.state = {
			isIE:
				window.navigator.userAgent.indexOf('Edge') > -1 ||
				(!!window.MSInputMethodContext && !!document.documentMode),
		};
	}
	static propTypes = {
		orient: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
		triggerState: PropTypes.object,
		title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		availableSpace: PropTypes.object,
		className: PropTypes.string,
		style: PropTypes.object,
		popupWidth: PropTypes.string,
	};
	static defaultProps = {
		className: '',
		style: {},
	};

	componentDidMount() {
		document.body.appendChild(this.tooltip);
		this._updatePosition();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.availableSpace !== this.props.availableSpace) {
			this._updatePosition();
		}
	}

	componentWillUnmount() {
		document.body.removeChild(this.tooltip);
	}

	_carbonId = `carbon_tooltio__${uniqueId()}`;
	_updateWidth() {
		requestAnimationFrame(() => {
			const tooltip = document.getElementById(this._carbonId);
			if (tooltip) {
				tooltip.style.minWidth = 0;
				tooltip.style.maxWidth = this.props.popupWidth;
				tooltip.style.width = this._getWidth();
			}
		});
	}
	_updatePosition() {
		if (this.props.availableSpace) {
			this.newOrient = domUtils.autoPosition(
				this.props.availableSpace,
				{
					height: this._box.clientHeight + 10,
					width: this.state.isIE ? 200 : this._box.firstChild.clientWidth,
				},
				this.props.orient
			);
		}

		const actualOrient = this.newOrient ? this.newOrient : this.props.orient;
		const tooltipStyle = this._getStyle(this._box.getBoundingClientRect().width, actualOrient);

		this._box.setAttribute(
			'style',
			`top: ${tooltipStyle.top}px; left: ${tooltipStyle.left}px; min-width: ${
				this._box.getBoundingClientRect().width
			}px`
		);
		this._updateWidth();
	}

	_getStyle(tooltipBoxWidth, orient) {
		const orientsList = [cx('top'), cx('right'), cx('bottom'), cx('left')];
		orientsList.map(item => this.tooltip.firstElementChild.classList.remove(item));
		this.tooltip.firstElementChild.classList.add(cx(orient));

		const { triggerState } = this.props;

		let tooltipStyle;
		if (orient === 'left') {
			tooltipStyle = {
				left: -parseInt(tooltipBoxWidth, 10),
				top: parseInt(triggerState.height, 10) / 2,
			};
		} else if (orient === 'right') {
			tooltipStyle = {
				left: parseInt(triggerState.width, 10),
				top: parseInt(triggerState.height, 10) / 2,
			};
		} else if (orient === 'top') {
			tooltipStyle = { top: 0, left: parseInt(triggerState.width, 10) / 2 };
		} else {
			tooltipStyle = {
				top: parseInt(triggerState.height, 10),
				left: parseInt(triggerState.width, 10) / 2,
			};
		}

		return tooltipStyle;
	}

	_getWidth() {
		const { triggerState } = this.props;

		return this.state.isIE
			? Math.min(parseInt(`${window.innerWidth - (triggerState.left || 0)}`, 10), 200)
			: parseInt(`${triggerState.width || 0}`, 10);
	}

	_ref = node => {
		this._box = node;
	};

	render() {
		const { className, title, triggerState, style, textDir, popupWidth, ...others } = this.props;
		const containerWidth = this._getWidth();

		return ReactDOM.createPortal(
			<div
				{...sanitize(others, TooltipPopup)}
				className={`${tooltipPopupClassName}__container ${className}`}
				style={{
					top: parseInt(`${triggerState.top || 0}`, 10),
					left: parseInt(`${triggerState.left || 0}`, 10),
					width: containerWidth,
					maxWidth: popupWidth,
					...style,
				}}
			>
				<div
					className={`${tooltipPopupClassName}__box`}
					ref={this._ref}
					style={{ minWidth: popupWidth }}
					dir={resolveBaseTextDir(title, textDir, false)}
				>
					<CarbonTooltip
						tooltipId={this._carbonId}
						triggerText={null}
						showIcon={false}
						open
						menuOffset={{
							top: 0,
							left: 0,
						}}
					>
						{title}
					</CarbonTooltip>
				</div>
			</div>,
			this.tooltip
		);
	}
}
